import React, { useState, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Select,
  MenuItem,
  FormControl,
  styled,
  Stack,
  Button,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { format } from "date-fns";
import { MonthFilter, Nodata, useAuth } from "../../sdk";
import MultiSelectFilter from "./MultiSelectFilters";
import {
  TableChart,
  TableRows,
  ArrowDropDownCircle,
} from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

const formatNumber = (value, decimalPoints = 0) => {
  if (value === null || value === -1 || value === undefined || isNaN(value)) {
    return "-";
  }
  return value.toFixed(decimalPoints);
};

const FIELD_OPTIONS = [
  { id: "LastYear", label: "Last Year" },
  { id: "Budget", label: "Budget" },
  { id: "LockedForecast", label: "Locked Forecast" },
  { id: "BOB", label: "BOB" },
  { id: "ActualForecast", label: "Actual/ Forecast" },
];
const FIELD_ORDER_MAP = FIELD_OPTIONS.reduce((acc, field, index) => {
  acc[field.id] = index;
  return acc;
}, {});

export default function ForecastSheetDailyTableView({
  jsonData,
  comparitiveDate,
  setComparitiveDate,
}) {
  const history = useHistory();
  const { currentHotel } = useAuth();
  const searchParams = new URLSearchParams(history.location.search);
  const [selectedSegment, setSelectedSegment] = useState(0);
  const [selectedFields, setSelectedFields] = useState(
    FIELD_OPTIONS.map((opt) => opt.id)
  );
  const [expandedSections, setExpandedSections] = useState({
    rooms: false,
    adr: false,
    revenue: false,
  });

  const noFieldsSelected = selectedFields.length === 0;

  // Parse the data
  const data = useMemo(() => {
    if (Array.isArray(jsonData)) {
      return jsonData;
    }
    return [jsonData];
  }, [jsonData]);

  // Get unique segments
  const segments = useMemo(() => {
    return data.filter(
      (item) => item?.segmentId && item?.systemName !== "Total"
    );
  }, [data]);

  // Get current segment data
  const currentSegment = useMemo(() => {
    return segments[selectedSegment];
  }, [segments, selectedSegment]);

  // Get budget model entries (excluding total rows)
  const budgetRows = useMemo(() => {
    if (!currentSegment || !currentSegment.budgetModel) return [];
    return currentSegment.budgetModel.filter((row) => row?.segmentID !== 0);
  }, [currentSegment]);

  // Calculate totals for all fields
  const totalRow = useMemo(() => {
    if (!budgetRows.length) return null;

    const totals = {
      roomsLY: 0,
      arrLY: 0,
      revenueLY: 0,
      roomsBudget: 0,
      arrBudget: 0,
      revenueBudget: 0,
      roomsLocked: 0,
      arrLocked: 0,
      revenueLocked: 0,
      roomsOTB: 0,
      arrOTB: 0,
      revenueOTB: 0,
      roomsActual: 0,
      arrActual: 0,
      revenueActual: 0,
    };

    budgetRows.forEach((row) => {
      Object.keys(totals).forEach((key) => {
        if (row[key] && row[key] !== -1) {
          totals[key] += row[key];
        }
      });
    });

    // Calculate ADR: Sum of all revenue / Sum of all rooms
    totals.arrLY = totals.roomsLY > 0 ? totals.revenueLY / totals.roomsLY : 0;
    totals.arrBudget =
      totals.roomsBudget > 0 ? totals.revenueBudget / totals.roomsBudget : 0;
    totals.arrLocked =
      totals.roomsLocked > 0 ? totals.revenueLocked / totals.roomsLocked : 0;
    totals.arrOTB =
      totals.roomsOTB > 0 ? totals.revenueOTB / totals.roomsOTB : 0;
    totals.arrActual =
      totals.roomsActual > 0 ? totals.revenueActual / totals.roomsActual : 0;

    return totals;
  }, [budgetRows]);

  // Get data value based on metric and field
  const getCellValue = (row, metric, fieldId) => {
    const fieldMap = {
      LastYear: {
        ROOMS: row?.roomsLY,
        ARR: row?.arrLY,
        REVENUE: row?.revenueLY,
      },
      Budget: {
        ROOMS: row?.roomsBudget,
        ARR: row?.arrBudget,
        REVENUE: row?.revenueBudget,
      },
      BOB: {
        ROOMS: row?.roomsOTB,
        ARR: row?.arrOTB,
        REVENUE: row?.revenueOTB,
      },
      ActualForecast: {
        ROOMS: row?.roomsActual,
        ARR: row?.arrActual,
        REVENUE: row?.revenueActual,
      },
      LockedForecast: {
        ROOMS: row?.roomsLocked,
        ARR: row?.arrLocked,
        REVENUE: row?.revenueLocked,
      },
    };

    const value = fieldMap[fieldId]?.[metric];
    const decimalPoints = metric === "ARR" ? 2 : 0;
    return formatNumber(value, decimalPoints);
  };

  const fieldsForPickup = useMemo(
    () => selectedFields.filter((f) => f !== "BOB"),
    [selectedFields]
  );

  const getPickupValue = (row, metric, fieldId) => {
    // Don't show pickup for BOB
    if (fieldId === "BOB") {
      return "-";
    }

    // BOB is always the base
    const bobMap = {
      ROOMS: row?.roomsOTB,
      ARR: row?.arrOTB,
      REVENUE: row?.revenueOTB,
    };

    const targetMap = {
      LastYear: {
        ROOMS: row?.roomsLY,
        ARR: row?.arrLY,
        REVENUE: row?.revenueLY,
      },
      Budget: {
        ROOMS: row?.roomsBudget,
        ARR: row?.arrBudget,
        REVENUE: row?.revenueBudget,
      },
      LockedForecast: {
        ROOMS: row?.roomsLocked,
        ARR: row?.arrLocked,
        REVENUE: row?.revenueLocked,
      },
      ActualForecast: {
        ROOMS: row?.roomsActual,
        ARR: row?.arrActual,
        REVENUE: row?.revenueActual,
      },
    };

    const target = targetMap[fieldId]?.[metric];
    const bob = bobMap[metric];

    if (target == null || bob == null || target === -1 || bob === -1) {
      return "-";
    }

    const pickup = Math.round(target - bob);
    const decimalPoints = metric === "ARR" ? 2 : 0;

    return formatNumber(pickup, decimalPoints);
  };

  const pickupTotals = useMemo(() => {
    const totals = {
      rooms: {},
      revenue: {},
      adr: {},
    };

    if (!totalRow) return totals;

    selectedFields.forEach((fieldId) => {
      // Don't calculate pickup for BOB
      if (fieldId === "BOB") {
        totals.rooms[fieldId] = 0;
        totals.revenue[fieldId] = 0;
        totals.adr[fieldId] = 0;
        return;
      }

      const suffixMap = {
        LastYear: "LY",
        Budget: "Budget",
        LockedForecast: "Locked",
        ActualForecast: "Actual",
      };
      const suffix = suffixMap[fieldId];

      if (!suffix) {
        return;
      }

      const targetRooms = totalRow[`rooms${suffix}`];
      const targetRevenue = totalRow[`revenue${suffix}`];
      const targetAdr = totalRow[`arr${suffix}`];

      totals.rooms[fieldId] =
        targetRooms === 0 ? 0 : Math.round(targetRooms - totalRow.roomsOTB);
      totals.revenue[fieldId] =
        targetRevenue === 0
          ? 0
          : Math.round(targetRevenue - totalRow.revenueOTB);
      totals.adr[fieldId] =
        targetAdr === 0 ? 0 : Math.round(targetAdr - totalRow.arrOTB);

      /* Uncomment if you want to calculate pickup 
      totals.rooms[fieldId] = (targetRooms || 0) - (totalRow.roomsOTB || 0);
      totals.revenue[fieldId] =
        (targetRevenue || 0) - (totalRow.revenueOTB || 0);
      totals.adr[fieldId] = (targetAdr || 0) - (totalRow.arrOTB || 0);
      */
    });

    return totals;
  }, [totalRow, selectedFields]);

  // Get field label for display
  const getFieldLabel = (fieldId) => {
    return FIELD_OPTIONS.find((opt) => opt.id === fieldId)?.label || fieldId;
  };

  const handleFieldChange = (fields) => {
    const sortedFields = [...fields].sort(
      (a, b) => FIELD_ORDER_MAP[a] - FIELD_ORDER_MAP[b]
    );
    setSelectedFields(sortedFields);
  };

  const handleAllButtonClick = () => {
    setSelectedFields(FIELD_OPTIONS.map((opt) => opt.id));
  };

  const handleChangeView = (mode) => {
    searchParams.set("view", mode);
    history.replace({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });
    history.push(`/hotel/${currentHotel?.id}/forecast/forecast_sheet_daily`);
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Wrapper>
      <Stack className="header">
        <Stack direction="row" gap={2}>
          <StyledPaper elevation={1}>
            <MonthFilter
              value={comparitiveDate}
              onChange={setComparitiveDate}
            />
          </StyledPaper>
          <StyledPaper elevation={1}>
            <FormControl>
              <StyledSelect
                value={selectedSegment}
                onChange={(e) => setSelectedSegment(e.target.value)}
                label="Segment"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 48 * 5.5,
                    },
                  },
                }}
              >
                {segments.map((seg, idx) => (
                  <MenuItem key={idx} value={idx}>
                    {seg.systemName}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </StyledPaper>

          <StyledPaper>
            <MultiSelectFilter
              FIELD_OPTIONS={FIELD_OPTIONS}
              selectedFields={selectedFields}
              handleFieldChange={handleFieldChange}
            />
          </StyledPaper>

          <Button variant="contained" onClick={handleAllButtonClick}>
            All
          </Button>
        </Stack>
        <Box className="view-box">
          <StyledToggleButtonGroup
            value={"single-segment"}
            exclusive
            color="primary"
          >
            <ToggleButton value="all" onClick={() => handleChangeView("all")}>
              <TableRows />
            </ToggleButton>
            <ToggleButton value="single-segment">
              <TableChart />
            </ToggleButton>
          </StyledToggleButtonGroup>
        </Box>
      </Stack>

      {noFieldsSelected ? (
        <Nodata message="Please select at least one field." />
      ) : budgetRows.length === 0 ? (
        <Nodata />
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: 2,
            height: "calc(100vh - 150px)",
            overflowY: "auto",
            overflowX:
              expandedSections.rooms ||
              expandedSections.adr ||
              expandedSections.revenue
                ? "auto"
                : "hidden",
          }}
        >
          <Table
            sx={{
              minWidth:
                expandedSections.rooms ||
                expandedSections.adr ||
                expandedSections.revenue
                  ? 2000
                  : 1200,
            }}
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <StyledHeaderCell rowSpan={2}>Date</StyledHeaderCell>
                <StyledHeaderCellWithIcon colSpan={selectedFields.length}>
                  <IconWrapper>
                    <span>Rooms</span>
                    <ExpandIcon
                      expanded={expandedSections.rooms}
                      onClick={() => toggleSection("rooms")}
                    />
                  </IconWrapper>
                </StyledHeaderCellWithIcon>

                {expandedSections.rooms && (
                  <StyledHeaderCell colSpan={fieldsForPickup.length} isPickup>
                    Pickup Required to Acheive
                  </StyledHeaderCell>
                )}
                <StyledHeaderCellWithIcon colSpan={selectedFields.length}>
                  <IconWrapper>
                    <span>ADR</span>
                    <ExpandIcon
                      expanded={expandedSections.adr}
                      onClick={() => toggleSection("adr")}
                    />
                  </IconWrapper>
                </StyledHeaderCellWithIcon>

                {expandedSections.adr && (
                  <StyledHeaderCell colSpan={fieldsForPickup.length} isPickup>
                    Pickup Required to Acheive
                  </StyledHeaderCell>
                )}
                <StyledHeaderCellWithIcon colSpan={selectedFields.length}>
                  <IconWrapper>
                    <span>Revenue</span>
                    <ExpandIcon
                      expanded={expandedSections.revenue}
                      onClick={() => toggleSection("revenue")}
                    />
                  </IconWrapper>
                </StyledHeaderCellWithIcon>

                {expandedSections.revenue && (
                  <StyledHeaderCell colSpan={fieldsForPickup.length} isPickup>
                    Pickup Required to Acheive
                  </StyledHeaderCell>
                )}
              </TableRow>

              {/* Sub-header row */}
              <TableRow>
                {selectedFields.map((fieldId) => (
                  <StyledSubHeaderCell key={`rooms-${fieldId}`}>
                    {getFieldLabel(fieldId)}
                  </StyledSubHeaderCell>
                ))}
                {expandedSections.rooms &&
                  selectedFields
                    .filter((f) => f !== "BOB")
                    .map((fieldId) => (
                      <StyledSubHeaderCell
                        key={`pickup-rooms-${fieldId}`}
                        isPickup
                      >
                        {getFieldLabel(fieldId)}
                      </StyledSubHeaderCell>
                    ))}
                {selectedFields.map((fieldId) => (
                  <StyledSubHeaderCell key={`arr-${fieldId}`}>
                    {getFieldLabel(fieldId)}
                  </StyledSubHeaderCell>
                ))}
                {expandedSections.adr &&
                  selectedFields
                    .filter((f) => f !== "BOB")
                    .map((fieldId) => (
                      <StyledSubHeaderCell
                        key={`pickup-arr-${fieldId}`}
                        isPickup
                      >
                        {getFieldLabel(fieldId)}
                      </StyledSubHeaderCell>
                    ))}
                {selectedFields.map((fieldId) => (
                  <StyledSubHeaderCell key={`revenue-${fieldId}`}>
                    {getFieldLabel(fieldId)}
                  </StyledSubHeaderCell>
                ))}
                {expandedSections.revenue &&
                  selectedFields
                    .filter((f) => f !== "BOB")
                    .map((fieldId) => (
                      <StyledSubHeaderCell
                        key={`pickup-revenue-${fieldId}`}
                        isPickup
                      >
                        {getFieldLabel(fieldId)}
                      </StyledSubHeaderCell>
                    ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {budgetRows.map((row, idx) => (
                <StyledTableRow key={idx}>
                  <DateCell>
                    {format(new Date(row.date), "dd/MM")}{" "}
                    {format(new Date(row.date), "EEE")}
                  </DateCell>

                  {/* Rooms columns */}
                  {selectedFields.map((fieldId, index) => (
                    <StyledTableCell
                      key={`rooms-${idx}-${fieldId}`}
                      islast={index === selectedFields.length - 1}
                    >
                      {getCellValue(row, "ROOMS", fieldId)}
                    </StyledTableCell>
                  ))}

                  {/* Pickup Required to Achieve Rooms columns */}
                  {expandedSections.rooms &&
                    selectedFields
                      .filter((f) => f !== "BOB")
                      .map((fieldId, index) => (
                        <StyledTableCell
                          key={`pickup-rooms-${idx}-${fieldId}`}
                          islast={index === fieldsForPickup.length - 1}
                        >
                          {getPickupValue(row, "ROOMS", fieldId)}
                        </StyledTableCell>
                      ))}

                  {/* ARR columns */}
                  {selectedFields.map((fieldId, index) => (
                    <StyledTableCell
                      key={`arr-${idx}-${fieldId}`}
                      islast={index === selectedFields.length - 1}
                    >
                      {getCellValue(row, "ARR", fieldId)}
                    </StyledTableCell>
                  ))}

                  {/* Pickup Required to Achieve ARR columns */}
                  {expandedSections.adr &&
                    selectedFields
                      .filter((f) => f !== "BOB")
                      .map((fieldId, index) => (
                        <StyledTableCell
                          key={`pickup-arr-${idx}-${fieldId}`}
                          islast={index === fieldsForPickup.length - 1}
                        >
                          {getPickupValue(row, "ARR", fieldId)}
                        </StyledTableCell>
                      ))}

                  {/* Revenue columns */}
                  {selectedFields.map((fieldId, index) => (
                    <StyledTableCell
                      key={`revenue-${idx}-${fieldId}`}
                      islast={index === selectedFields.length - 1}
                    >
                      {getCellValue(row, "REVENUE", fieldId)}
                    </StyledTableCell>
                  ))}

                  {/* Pickup Required to Achieve Revenue columns */}
                  {expandedSections.revenue &&
                    selectedFields
                      .filter((f) => f !== "BOB")
                      .map((fieldId) => (
                        <StyledTableCell
                          key={`pickup-revenue-${idx}-${fieldId}`}
                        >
                          {getPickupValue(row, "REVENUE", fieldId)}
                        </StyledTableCell>
                      ))}
                </StyledTableRow>
              ))}

              {/* Total Row */}
              {totalRow && (
                <TotalTableRow>
                  <TotalCell>Total</TotalCell>

                  {/* Rooms totals */}
                  {selectedFields.map((fieldId) => (
                    <TotalCell key={`rooms-total-${fieldId}`}>
                      {getCellValue(totalRow, "ROOMS", fieldId)}
                    </TotalCell>
                  ))}

                  {/* Pickup Required to Achieve Rooms totals */}
                  {expandedSections.rooms &&
                    selectedFields
                      .filter((f) => f !== "BOB")
                      .map((fieldId) => (
                        <TotalCell key={`pickup-rooms-total-${fieldId}`}>
                          {formatNumber(pickupTotals.rooms[fieldId])}
                        </TotalCell>
                      ))}

                  {/* ARR totals */}
                  {selectedFields.map((fieldId) => (
                    <TotalCell key={`arr-total-${fieldId}`}>
                      {getCellValue(totalRow, "ARR", fieldId)}
                    </TotalCell>
                  ))}

                  {/* Pickup Required to Achieve ARR totals */}
                  {expandedSections.adr &&
                    selectedFields
                      .filter((f) => f !== "BOB")
                      .map((fieldId) => (
                        <TotalCell key={`pickup-arr-total-${fieldId}`}>
                          {formatNumber(pickupTotals.adr[fieldId], 2)}
                        </TotalCell>
                      ))}

                  {/* Revenue totals */}
                  {selectedFields.map((fieldId) => (
                    <TotalCell key={`revenue-total-${fieldId}`}>
                      {getCellValue(totalRow, "REVENUE", fieldId)}
                    </TotalCell>
                  ))}

                  {/* Pickup Required to Achieve Revenue totals */}
                  {expandedSections.revenue &&
                    selectedFields
                      .filter((f) => f !== "BOB")
                      .map((fieldId) => (
                        <TotalCell key={`pickup-revenue-total-${fieldId}`}>
                          {formatNumber(pickupTotals.revenue[fieldId])}
                        </TotalCell>
                      ))}
                </TotalTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Wrapper>
  );
}

// Styled Components
const Wrapper = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(10, 3, 0, 10),
  ".header": {
    paddingLeft: theme.spacing(7),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  ".view-box": {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  borderBottom: `1px solid #e0e0e0`,
  fontSize: "14px",
}));

const StyledTableCell = styled(TableCell)(({ theme, islast }) => ({
  padding: theme.spacing(0.5, 1),
  fontWeight: 300,
  fontSize: "14px",
  textAlign: "center",
  ...(islast && { borderRight: "1px solid #c4c2c2ff" }),
}));

const StyledHeaderCell = styled(StyledTableCell)(({ theme,isPickup }) => ({
  backgroundColor: isPickup ? "#f1f6fc" : "#306FBC",
  border: "1px solid #ffffff",
  color: isPickup ? "#000000" : "#ffffff",
  fontWeight: 700,
  fontSize: "14px",
  textAlign: "center",
  position: "sticky",
  top: 0,
}));

const StyledHeaderCellWithIcon = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#306FBC",
  border: "1px solid #ffffff",
  color: "#ffffff",
  fontWeight: 700,
  fontSize: "14px",
  position: "sticky",
  top: 0,
  display: "table-cell",
  verticalAlign: "middle",
  flex: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "8px 1px",
  height: "auto",
  minWidth: "80px",
  overflow: "visible",
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  gap: theme.spacing(0.25),
  width: "100%",
  whiteSpace: "nowrap",
  paddingRight: "20px",
  overflow: "visible",
}));

const ExpandIcon = styled(ArrowDropDownCircle, {
  shouldForwardProp: (prop) => prop !== "expanded",
})(({ expanded }) => ({
  fontSize: "28px",
  cursor: "pointer",
  color: "#ffffff",
  transition: "transform 0.3s ease",
  position: "absolute",
  top: "50%",
  right: "-5px",
  transform: expanded
    ? "translateY(-50%) rotate(90deg)"
    : "translateY(-50%) rotate(-90deg)",
  zIndex: 100,
  overflow: "visible",
}));

const StyledSubHeaderCell = styled(StyledTableCell)(
  ({ theme, isPickup = false }) => ({
    backgroundColor: !isPickup ? "#306FBC" : "#f1f6fc",
    color: !isPickup ? "#ffffff" : "",
    fontWeight: 700,
    fontSize: "14px",
    border: "1px solid #ffffff",
    padding: theme.spacing(0.5, 0.5),
    position: "sticky",
    top: theme.spacing(5.2),
    zIndex: 100,
    textAlign: "center",
  })
);

const DateCell = styled(StyledTableCell)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: 300,
  textAlign: "center",
  borderRight: "1px solid #c4c2c2ff",
  padding: theme.spacing(0.5, 1),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: theme.spacing(5),
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: theme.spacing(0.5),
  fontSize: theme.spacing(2),
  fontWeight: 400,
  color: "#000",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  height: theme.spacing(5),
  boxShadow: "0px 4px 4px rgba(3, 4, 94, 0.2)",
  borderRadius: "8px",
}));

const TotalTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "#306FBC",
  borderTop: `2px solid #1e4a7a`,
}));

const TotalCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  fontWeight: 700,
  fontSize: "14px",
  textAlign: "center",
  color: "#ffffff",
  border: "1px solid #ffffff",
  position: "sticky",
  bottom: 0,
  backgroundColor: "#306FBC",
  zIndex: 100,
}));
